import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Stack,
  Chip,
  CircularProgress,
  Pagination,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import {
  apiStringToDatetimeLocalInput,
  datetimeLocalToApiString,
  formatTurkeyLocalIntl,
  toTurkeyLocalDateOnly,
} from '../../utils/turkeyLocalDateTime';
import { getCurrencyDisplayLabel } from '../../utils/currencyDisplay';
import {
  fetchAllMeetings,
  createMeeting,
  createMeetingWithGallery,
  updateMeeting,
  deleteMeeting,
  toggleMeetingActiveStatus,
  fetchMemberAttendanceSummaries,
} from '../../controllers/meetingsController';
import EntityGalleryFormSection from '../../components/EntityGalleryFormSection';
import { buildCreateEntityGalleryDto, initialEntityGalleryForm } from '../../constants/galleryEntityTypes';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import EditMeetingDialog from './EditMeetingDialog';
import MeetingMealPriceField, {
  useWeeklyMeetingMealConfig,
} from './components/MeetingMealPriceField';

const WeeklyMeetings = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  // Form states
  const [form, setForm] = useState({
    title: '',
    meetingDate: '',
    meetingEndDate: '',
    meetingRoomName: '',
    location: '',
    onlineMeetingUrl: '',
    description: '',
    allowGuests: false,
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const [galleryForm, setGalleryForm] = useState(initialEntityGalleryForm);
  const {
    loading: mealConfigLoading,
    error: mealConfigError,
    mealPrice: configMealPrice,
    currency: configCurrency,
  } = useWeeklyMeetingMealConfig(createDialogOpen);

  // Member weekly attendance summaries
  const [memberSummaries, setMemberSummaries] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [memberStartDate, setMemberStartDate] = useState('');
  const [memberEndDate, setMemberEndDate] = useState('');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');

  // Load meetings function
  const loadMeetings = async (overridePage = null) => {
    setLoading(true);
    setError('');
    try {
      const { data, meta } = await fetchAllMeetings({
        pageNumber: overridePage !== null ? overridePage : page,
        pageSize,
        startDate: startDate || null,
        endDate: endDate || null,
        isActive: isActiveFilter === '' ? null : isActiveFilter === 'true',
        searchTerm: searchTerm || null,
      });
      setMeetings(data || []);
      setTotalPages(meta?.totalPages || 1);
    } catch (e) {
      setError(e?.message || 'Toplantılar alınamadı');
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  // Load meetings
  useEffect(() => {
    loadMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, startDate, endDate, isActiveFilter, searchTerm]);

  const loadMemberSummaries = async () => {
    setMemberLoading(true);
    setMemberError('');
    try {
      const data = await fetchMemberAttendanceSummaries({
        startDate: memberStartDate,
        endDate: memberEndDate,
        searchTerm: memberSearchTerm || null,
      });
      setMemberSummaries(Array.isArray(data) ? data : []);
    } catch (e) {
      setMemberError(e?.message || 'Üye katılım özetleri alınamadı');
      setMemberSummaries([]);
    } finally {
      setMemberLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== 1) return;

    // Eğer tarih seçilmediyse default olarak son 6 ayı ayarla
    if (!memberStartDate || !memberEndDate) {
      const today = new Date();
      const to = new Date(today);
      to.setDate(to.getDate() + 1); // güvenli olması için bugünden 1 gün sonrası

      const from = new Date(today);
      from.setMonth(from.getMonth() - 6); // son 6 ay

      const toStr = toTurkeyLocalDateOnly(to);
      const fromStr = toTurkeyLocalDateOnly(from);

      setMemberStartDate(fromStr);
      setMemberEndDate(toStr);
      return;
    }

    loadMemberSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, memberStartDate, memberEndDate, memberSearchTerm]);

  const handleOpenCreateDialog = () => {
    setForm({
      title: '',
      meetingDate: '',
      meetingEndDate: '',
      meetingRoomName: '',
      location: '',
      onlineMeetingUrl: '',
      description: '',
      allowGuests: false,
      isActive: true,
    });
    setGalleryForm(initialEntityGalleryForm);
    setCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setForm({
      title: meeting.title || '',
      meetingDate: apiStringToDatetimeLocalInput(meeting.meetingDate),
      meetingEndDate: apiStringToDatetimeLocalInput(meeting.meetingEndDate),
      meetingRoomName: meeting.meetingRoomName || '',
      location: meeting.location || '',
      onlineMeetingUrl: meeting.onlineMeetingUrl || '',
      description: meeting.description || '',
      allowGuests: meeting.allowGuests || false,
      isActive: meeting.isActive !== undefined ? meeting.isActive : true,
    });
    setEditDialogOpen(true);
  };


  const handleCreate = async () => {
    if (!form.title || !form.meetingDate) {
      toast.error('Başlık ve toplantı tarihi zorunludur');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        meetingDate: datetimeLocalToApiString(form.meetingDate),
        meetingEndDate: datetimeLocalToApiString(form.meetingEndDate),
        meetingRoomName: form.meetingRoomName?.trim() || null,
        location: form.location || null,
        onlineMeetingUrl: form.onlineMeetingUrl || null,
        description: form.description || null,
        mealPrice: configMealPrice,
        currency: configCurrency,
        allowGuests: form.allowGuests,
        isActive: form.isActive,
      };
      const galleryDto = buildCreateEntityGalleryDto(galleryForm);
      if (galleryDto) payload.gallery = galleryDto;

      const hasGalleryImages = galleryForm.enabled && (galleryForm.files?.length || 0) > 0;
      if (hasGalleryImages) {
        await createMeetingWithGallery({ dto: payload, galleryImages: galleryForm.files });
      } else {
        await createMeeting(payload);
      }
      toast.success('Toplantı başarıyla oluşturuldu');
      setCreateDialogOpen(false);
      setPage(1);
      // Reload meetings list (go to page 1)
      await loadMeetings(1);
    } catch (e) {
      toast.error(e?.message || 'Toplantı oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.title || !form.meetingDate) {
      toast.error('Başlık ve toplantı tarihi zorunludur');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: selectedMeeting.id,
        title: form.title,
        meetingDate: datetimeLocalToApiString(form.meetingDate),
        meetingEndDate: datetimeLocalToApiString(form.meetingEndDate),
        meetingRoomName: form.meetingRoomName?.trim() || null,
        location: form.location || null,
        onlineMeetingUrl: form.onlineMeetingUrl || null,
        description: form.description || null,
        mealPrice: configMealPrice,
        currency: configCurrency,
        allowGuests: form.allowGuests,
        isActive: form.isActive,
      };

      await updateMeeting(payload);
      toast.success('Toplantı başarıyla güncellendi');
      setEditDialogOpen(false);
      setSelectedMeeting(null);
      // Reload meetings list
      await loadMeetings();
    } catch (e) {
      toast.error(e?.message || 'Toplantı güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMeeting) return;
    setSaving(true);
    try {
      await deleteMeeting(selectedMeeting.id);
      toast.success('Toplantı başarıyla silindi');
      setDeleteDialogOpen(false);
      setSelectedMeeting(null);
      // Reload meetings list
      await loadMeetings();
    } catch (e) {
      toast.error(e?.message || 'Toplantı silinemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (meeting) => {
    try {
      await toggleMeetingActiveStatus(meeting.id);
      toast.success(`Toplantı ${meeting.isActive ? 'pasif' : 'aktif'} yapıldı`);
      // Reload meetings list
      await loadMeetings();
    } catch (e) {
      toast.error(e?.message || 'Toplantı durumu değiştirilemedi');
    }
  };

  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setIsActiveFilter('');
    setSearchTerm('');
    setPage(1);
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
    },
    '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
  };

  const multilineTextFieldSx = {
    ...textFieldSx,
    '& .MuiInputBase-input': { fontSize: '14px', px: 0, py: 0 },
  };

  const tableCellHeadSx = {
    fontSize: '14px',
    fontWeight: 500,
    borderBottom: '1px dashed #e8e8e8',
    px: 1.5,
    py: 1.5,
    color: '#a3a3a3',
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Haftalık Toplantılar</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ width: 16 }} />}
          onClick={handleOpenCreateDialog}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E7EDF4',
            boxShadow: 'none',
            fontSize: 13,
            borderRadius: '5px',
            color: '#0E4D94',
            '&:hover': {
              backgroundColor: '#CFDBEA',
              boxShadow: 'none',
            },
          }}
        >
          Yeni Toplantı
        </Button>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#a3a3a3',
            '&.Mui-selected': { color: '#0E4D94' },
          },
          '& .MuiTabs-indicator': { backgroundColor: '#0E4D94' },
        }}
      >
        <Tab label="Toplantı Listesi" />
        <Tab label="Üye Katılım Özetleri" />
      </Tabs>

      {tab === 0 && (
        <>
          {/* Filters */}
          <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', mb: 2 }}>
            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 2 }}>
              Filtreler
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Ara
                  </Typography>
                  <TextField
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Başlık ile ara..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-root': { minHeight: 40 },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Başlangıç Tarihi
                  </Typography>
                  <TextField
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-root': { minHeight: 40 },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Bitiş Tarihi
                  </Typography>
                  <TextField
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-root': { minHeight: 40 },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ minWidth: 140 }}>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Durum
                  </Typography>
                  <TextField
                    select
                    value={isActiveFilter}
                    onChange={(e) => setIsActiveFilter(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-root': { minHeight: 40 },
                    }}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    <MenuItem value="true">Aktif</MenuItem>
                    <MenuItem value="false">Pasif</MenuItem>
                  </TextField>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      borderRadius: '6px',
                      borderColor: '#e6e6e6',
                      color: '#444444',
                      minHeight: 40,
                      '&:hover': {
                        borderColor: '#0E4D94',
                        color: '#0E4D94',
                        backgroundColor: 'rgba(14, 77, 148, 0.04)',
                      },
                    }}
                  >
                    Filtreleri Temizle
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Table */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '13px' }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#0E4D94' }} />
            </Box>
          ) : meetings.length === 0 ? (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f5f5f5',
                py: 1,
                px: 2.5,
              }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={tableCellHeadSx}>Başlık</TableCell>
                    <TableCell sx={tableCellHeadSx}>Tarih</TableCell>
                    <TableCell sx={tableCellHeadSx}>Lokasyon</TableCell>
                    <TableCell sx={tableCellHeadSx}>Yemek Ücreti</TableCell>
                    <TableCell sx={tableCellHeadSx}>Durum</TableCell>
                    <TableCell align="right" sx={tableCellHeadSx}>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>Toplantı bulunamadı.</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f5f5f5',
                  py: 1,
                  px: 2.5,
                }}
              >
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'transparent' }}>
                      <TableRow>
                        <TableCell sx={tableCellHeadSx}>Başlık</TableCell>
                        <TableCell sx={tableCellHeadSx}>Tarih</TableCell>
                        <TableCell sx={tableCellHeadSx}>Lokasyon</TableCell>
                        <TableCell sx={tableCellHeadSx}>Yemek Ücreti</TableCell>
                        <TableCell sx={tableCellHeadSx}>Durum</TableCell>
                        <TableCell align="right" sx={tableCellHeadSx}>
                          İşlemler
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {meetings.map((meeting, index) => (
                        <TableRow
                          key={meeting.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '& .MuiTableCell-root': {
                              borderBottom: index === meetings.length - 1 ? 'none' : '1px dashed #e8e8e8',
                              px: 1.5,
                              py: 1.5,
                              color: '#444444',
                              fontSize: '14px',
                              fontWeight: 500,
                            },
                            '&:hover': { backgroundColor: '#fafafa' },
                          }}
                          onClick={() => navigate(`/events/weekly-meetings/${meeting.id}`)}
                        >
                          <TableCell>{meeting.title}</TableCell>
                          <TableCell>
                            {formatTurkeyLocalIntl(meeting.meetingDate)}
                          </TableCell>
                          <TableCell>{meeting.location || '-'}</TableCell>
                          <TableCell>
                            {meeting.mealPrice
                              ? `${meeting.mealPrice.toLocaleString('tr-TR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })} ${getCurrencyDisplayLabel(meeting.currency)}`
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={meeting.isActive ? 'Aktif' : 'Pasif'}
                                size="small"
                                sx={{
                                  fontSize: '12px',
                                  ...(meeting.isActive
                                    ? { backgroundColor: '#E7EDF4', color: '#0E4D94' }
                                    : { backgroundColor: '#f5f5f5', color: '#666666' }),
                                }}
                              />
                              {meeting.isCompleted && (
                                <Chip
                                  label="Tamamlandı"
                                  size="small"
                                  sx={{
                                    fontSize: '12px',
                                    backgroundColor: '#E8F5E9',
                                    color: '#2E7D32',
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Düzenle">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEditDialog(meeting);
                                  }}
                                  sx={{
                                    backgroundColor: '#E7EDF4',
                                    borderRadius: '6px',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { backgroundColor: '#CFDBEA' },
                                    '& svg': { fontSize: '16px', color: '#0E4D94' },
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedMeeting(meeting);
                                    setDeleteDialogOpen(true);
                                  }}
                                  sx={{
                                    backgroundColor: '#FFEEF3',
                                    borderRadius: '6px',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { backgroundColor: '#FFD6E0' },
                                    '& svg': { fontSize: '14px', color: '#F8285A' },
                                  }}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, v) => setPage(v)}
                      sx={{
                        '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#0E4D94' },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
        </>
      )}

      {tab === 1 && (
        <>
          {/* Member attendance summary filters */}
          <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', mb: 2 }}>
            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 2 }}>
              Filtreler
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Dönem Başlangıç
                  </Typography>
                  <TextField
                    type="date"
                    value={memberStartDate}
                    onChange={(e) => setMemberStartDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-root': { minHeight: 40 },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Dönem Bitiş
                  </Typography>
                  <TextField
                    type="date"
                    value={memberEndDate}
                    onChange={(e) => setMemberEndDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-root': { minHeight: 40 },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Üye / Alt Üye Ara
                  </Typography>
                  <TextField
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="İsim ile ara"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-root': { minHeight: 40 },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>

          {memberError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '13px' }}>
              {memberError}
            </Alert>
          )}

          {memberLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#0E4D94' }} />
            </Box>
          ) : memberSummaries.length === 0 ? (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f5f5f5',
                py: 1,
                px: 2.5,
              }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={tableCellHeadSx}>Üye</TableCell>
                    <TableCell sx={tableCellHeadSx}>Dönem</TableCell>
                    <TableCell sx={tableCellHeadSx}>Toplam Toplantı</TableCell>
                    <TableCell sx={tableCellHeadSx}>Katıldığı</TableCell>
                    <TableCell sx={tableCellHeadSx}>İzinli</TableCell>
                    <TableCell sx={tableCellHeadSx}>Gelemediği</TableCell>
                    <TableCell sx={tableCellHeadSx}>Katılım Oranı</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>Kayıt bulunamadı.</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f5f5f5',
                py: 1,
                px: 2.5,
              }}
            >
              <Table size="small">
                  <TableHead sx={{ bgcolor: 'transparent' }}>
                    <TableRow>
                      <TableCell sx={tableCellHeadSx}>Üye</TableCell>
                      <TableCell sx={tableCellHeadSx}>Dönem</TableCell>
                      <TableCell sx={tableCellHeadSx}>Toplam Toplantı</TableCell>
                      <TableCell sx={tableCellHeadSx}>Katıldığı</TableCell>
                      <TableCell sx={tableCellHeadSx}>İzinli</TableCell>
                      <TableCell sx={tableCellHeadSx}>Gelemediği</TableCell>
                      <TableCell sx={tableCellHeadSx}>Katılım Oranı</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {memberSummaries.map((item, index) => (
                      <TableRow
                        key={`${item.personId}-${index}`}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '& .MuiTableCell-root': {
                            borderBottom: index === memberSummaries.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            px: 1.5,
                            py: 1.5,
                            color: '#444444',
                            fontSize: '14px',
                            fontWeight: 500,
                          },
                          '&:hover': { backgroundColor: '#fafafa' },
                        }}
                        onClick={() => {
                          const targetMemberId =
                            item.type === 1 && item.parentMemberId ? item.parentMemberId : item.personId;
                          if (targetMemberId) {
                            navigate(`/members/${targetMemberId}`);
                          }
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              src={item.profilePictureUrl ? resolveGroupsAssetUrl(item.profilePictureUrl) : undefined}
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: item.profilePictureUrl ? 'transparent' : '#E7EDF4',
                                color: '#0E4D94',
                                fontSize: 16,
                                fontWeight: 600,
                              }}
                            >
                              {!item.profilePictureUrl && (item.fullName?.charAt(0) || '?')}
                            </Avatar>
                            <Stack spacing={0.25}>
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{item.fullName}</Typography>
                              {item.parentFullName && (
                                <Typography sx={{ fontSize: 12, color: '#888' }}>
                                  Bağlı Olduğu Üye: {item.parentFullName}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, color: '#555' }}>
                            {new Date(item.periodStart).toLocaleDateString('tr-TR')} -{' '}
                            {new Date(item.periodEnd).toLocaleDateString('tr-TR')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 14, color: '#444444', fontWeight: 500 }}>
                            {item.totalMeetings}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.attendedMeetings}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: '#DFFFEA',
                              color: '#17C653',
                              borderRadius: '12px',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.excusedMeetings}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: '#E7EDF4',
                              color: '#0E4D94',
                              borderRadius: '12px',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.absentMeetings}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: '#FFEEF3',
                              color: '#F8285A',
                              borderRadius: '12px',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${item.attendanceRate != null ? item.attendanceRate.toFixed(1) : '-'}%`}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor:
                                item.attendanceRate == null
                                  ? '#f5f5f5'
                                  : item.attendanceRate >= 80
                                    ? '#DFFFEA'
                                    : item.attendanceRate >= 50
                                      ? '#FFF4E6'
                                      : '#FFEEF3',
                              color:
                                item.attendanceRate == null
                                  ? '#666666'
                                  : item.attendanceRate >= 80
                                    ? '#17C653'
                                    : item.attendanceRate >= 50
                                      ? '#FF9800'
                                      : '#F8285A',
                              borderRadius: '12px',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Yeni Toplantı Oluştur
          <IconButton
            onClick={() => setCreateDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Başlık
              </Typography>
              <TextField
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                fullWidth
                required
                placeholder=""
                inputProps={{ maxLength: 200 }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Toplantı Tarihi
              </Typography>
              <TextField
                type="datetime-local"
                value={form.meetingDate}
                onChange={(e) => setForm({ ...form, meetingDate: e.target.value })}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Toplantı Bitiş Tarihi
              </Typography>
              <TextField
                type="datetime-local"
                value={form.meetingEndDate}
                onChange={(e) => setForm({ ...form, meetingEndDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Toplantı Salonu
              </Typography>
              <TextField
                value={form.meetingRoomName}
                onChange={(e) => setForm({ ...form, meetingRoomName: e.target.value })}
                fullWidth
                placeholder="Salon adı (opsiyonel)"
                inputProps={{ maxLength: 200 }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Lokasyon
              </Typography>
              <TextField
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                fullWidth
                placeholder=""
                inputProps={{ maxLength: 500 }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Online Toplantı URL
              </Typography>
              <TextField
                value={form.onlineMeetingUrl}
                onChange={(e) => setForm({ ...form, onlineMeetingUrl: e.target.value })}
                fullWidth
                inputProps={{ maxLength: 1000 }}
                placeholder="https://..."
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Açıklama
              </Typography>
              <TextField
                multiline
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                fullWidth
                placeholder=""
                inputProps={{ maxLength: 2000 }}
                sx={multilineTextFieldSx}
              />
            </Box>
            <Grid container spacing={2}>
              <MeetingMealPriceField
                loading={mealConfigLoading}
                error={mealConfigError}
                mealPrice={configMealPrice}
                currency={configCurrency}
                textFieldSx={textFieldSx}
              />
            </Grid>
            <EntityGalleryFormSection
              value={galleryForm}
              onChange={setGalleryForm}
              disabled={saving}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Misafir davet edilebilir mi?
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.allowGuests}
                    onChange={(e) => setForm({ ...form, allowGuests: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#0E4D94' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0E4D94' },
                    }}
                  />
                }
                label={form.allowGuests ? 'Evet' : 'Hayır'}
                sx={{ m: 0 }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Durum
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#0E4D94' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0E4D94' },
                    }}
                  />
                }
                label={form.isActive ? 'Aktif' : 'Pasif'}
                sx={{ m: 0 }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !form.title || !form.meetingDate}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              borderRadius: '5px',
              backgroundColor: '#0E4D94',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0a3a70', boxShadow: 'none' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      {selectedMeeting && (
        <EditMeetingDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedMeeting(null);
          }}
          meeting={selectedMeeting}
          onUpdated={(updated) => {
            setSelectedMeeting(null);
            setEditDialogOpen(false);
            loadMeetings();
          }}
        />
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Toplantıyı Sil
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            {selectedMeeting
              ? `"${selectedMeeting.title}" toplantısını silmek istediğinize emin misiniz?`
              : 'Bu toplantıyı silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#E01E4A',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e',
              },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeeklyMeetings;
